import { useApolloClient } from '@apollo/client';

import localforage from 'localforage';
import { useLiveWorkerGeneration } from 'domains/viewer/Viewer/EscapeHatch/state';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { purgePrecache } from 'modules/Apollo/links/precacheLink';
import { purgeNrrdMaskCache } from 'modules/nrrdDownloader';
import { logger } from 'modules/logger';
import {
  createPixelDataSharedWorker,
  PixelDataSharedWorker,
} from 'modules/viewer/workers/PixelDataSharedWorker';
import { useLayoutEffect } from 'react';

export const clearStoredData = async (pixelDataSharedWorker: PixelDataSharedWorker | null) => {
  try {
    // Clear localforage cache on logout so that we don't keep stale data and we allow users to
    // login with a different account and start from scratch
    // this is a temporary security mitigation until we have encrypted local storage
    await localforage.clear();
  } catch (e: any) {
    logger.error('problem clearing local storage on logout', e);
  }
  try {
    // Clear precache on logout so that we don't keep stale data and we allow users to
    // login with a different account and start from scratch
    // this is a temporary security mitigation until we have encrypted local storage
    await purgePrecache();
  } catch (e: any) {
    logger.error('problem clearing precache on logout', e);
  }
  try {
    // Clear the NRRD mask cache's LRUSWCache registry on logout (VX-4241) -- see
    // purgeNrrdMaskCache's own comment for why the generic caches.keys() wipe below isn't enough
    // on its own.
    await purgeNrrdMaskCache();
  } catch (e: any) {
    logger.error('problem clearing nrrd mask cache on logout', e);
  }
  try {
    // Clear pixel db on logout so that we don't keep stale data and we allow users to
    // login with a different account and start from scratch
    // this is a temporary security mitigation until we have encrypted local storage
    pixelDataSharedWorker?.port.postMessage({
      type: 'clear-cache',
    });
  } catch (e: any) {
    logger.error('problem clearing pixel data on logout', e);
  }
  // clear old items
  try {
    localStorage.removeItem('apollo-cache-persist');
    const cacheKeys = await caches.keys();
    await Promise.all(cacheKeys.map((key) => caches.delete(key)));
  } catch (e: any) {
    logger.error('problem clearing old caches', e);
  }
};

export const useClearStoredDataOnClose = (): void => {
  const apolloClient = useApolloClient();
  const [useDedupedPixelWorker] = useFeatureFlagEnabled(FF.PIXEL_WORKER_DEDUPLICATION);
  const generation = useLiveWorkerGeneration();

  useLayoutEffect(() => {
    const onPageHide = () => {
      // VX-4363: connect at the live generation, and the live worker variant, so clear-cache
      // reaches the worker actually holding this session's PHI-bearing pixel cache.
      clearStoredData(
        createPixelDataSharedWorker(useDedupedPixelWorker, undefined, false, generation)
      );
      try {
        apolloClient.cache.reset();
        apolloClient.resetStore();
        // eslint-disable-next-line no-empty
      } catch {}
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [apolloClient, useDedupedPixelWorker, generation]);
};
